import { useEffect, useRef } from 'react';
import ImageUploading from 'react-images-uploading';

import UserService from '@/services/user.service';

import { handleGenericSubmit } from '../../utils';

import ImageDisplay from './ImageDisplay';

import './BoardBodyPicture.css';

/**
 * Upload helper. Batches each onChange selection under one pictureId group:
 * 1. onChange: allocate one groupId for this batch
 * 2. Insert placeholders for each file with that same groupId
 * 3. Upload each file, replace its placeholder, and append one {type:'post_pictures',id:groupId}
 *    (only once per group)
 * 4. Expose the onImageUpload() trigger to BoardTextBody
 */

export default function ImageManager({
  formState,
  setFormState,
  editableRef,
  setTriggerImageUpload,
  textEditorSelector,
  previewMode,
  handleImageRemove,
  handleCaptionChange,
  postMode,
}) {
  const onImageUploadRef = useRef(null);

  // Handles a single file upload using the batch’s groupId
  async function handlePostImageUpload(file) {
    const { response, data } = await UserService.uploadImage(file);
    if (response.ok) {
      return data;
    } else {
      console.error(`[BoardBodyPicture] Upload ✗ file="${file.name}"`);
      alert('Something went wrong uploading your image.');
    }
  }

  const onImagesUploaded = async (imageList) => {

    const newGroupId   = (formState.post_pictures.length || 0) + 1;

    const groupPictures = { groupId: newGroupId, pictures: [] };

    // 2) Kick off uploads for each new index
    for(let idx = 0; idx < imageList.length; idx++) {
      const image = imageList[idx];
      const file = image.file;
      const dataResponse = await handlePostImageUpload(file);
      groupPictures.pictures.push({
        ...dataResponse,
        pictureId: idx + 1,
      });
    }

    const newPost_pictures = [...formState.post_pictures, groupPictures];

    setFormState(prev => ({
      ...prev,
      post_pictures: newPost_pictures,
    }));

    const containerId = `groupPicture-${newGroupId}`;
        
    handleGenericSubmit(textEditorSelector, editableRef, containerId,
      <ImageDisplay
        groupId={newGroupId}
        pictures={groupPictures.pictures}
        previewMode={previewMode}
        handleImageRemove={handleImageRemove}
        handleCaptionChange={handleCaptionChange}
      />, postMode);
  };

  useEffect(() => {
    setTriggerImageUpload(() => () => {
      onImageUploadRef.current?.();
    });
  }, [setTriggerImageUpload]);

  return (
    <div className="board-body-picture">
      <ImageUploading
        multiple
        maxNumber={10}
        maxFileSize={15_000_000}
        onChange={onImagesUploaded}
        onError={errs => {
          console.error('[BoardBodyPicture] onError:', errs);
          if (errs.maxNumber)   alert('Up to 10 images only.');
          if (errs.acceptType)  alert('Invalid image type.');
          if (errs.maxFileSize) alert('File too large (15 MB).');
        }}
      >
        {({ onImageUpload }) => {
          onImageUploadRef.current ||= onImageUpload;
          return null;
        }}
      </ImageUploading>
    </div>
  );
}
